import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uploadTerms from "@tKgpco/Handlers/Terms/UploadTerms";
import React, { useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    uploadTerms,
};

const mapStateToProps = (state: ApplicationState) => ({});

interface CustomWidgetStyles {
    wrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    uploadingButtonLabel?: TypographyPresentationProps;
    uploadFileButtonLabel?: TypographyPresentationProps;
    uploadFileButton?: ButtonPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileUploader?: FileUploadPresentationProps;
}

const styles: CustomWidgetStyles = {
    uploadFileButton: {
        css: css`
            /* float: right; */
            margin-top: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 10px;
                            width: 100%;
                        `,
                        css`
                            width: initial;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin-right: 8px;
        `,
    },
    uploadingButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            vertical-align: super;
        `,
    },
    uploadFileButtonLabel: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    downloadTemplateButton: {
        css: css`
            margin-bottom: 15px;
        `,
        variant: "secondary",
    },
    fileUploader: {
        cssOverrides: {
            formField: css`
                > div {
                    margin-top: 10px !important;
                }
            `,
        }
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ImportTerms: React.FC<Props> = ({ id, uploadTerms }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const [file, setFile] = useState<any>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["xls", "xlsx", "csv"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
        }
    };

    const uploadFileHandler = () => {
        if (isUploading) {
            return;
        }

        if (!file) {
            return;
        }

        uploadTerms({ file, onSuccess: onUploadingSuccess });
    };

    const onUploadingSuccess = React.useCallback(() => {
        setIsUploading(false);
        setIsOpen(false);
        toasterContext.addToast({ body: translate("Terms uploaded"), messageType: "success" });
    }, [file]);

    const downloadTemplateHandler = () => {
        window.open(`${window.location.origin}/userfiles/QuoteTermsImport.xlsx`);
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Clickable onClick={() => setIsOpen(true)} aria-label="Import Terms" title="Import Terms">
                    <Icon src="Upload" size={20} />
                </Clickable>
            </StyledWrapper>
            <Modal isOpen={isOpen} handleClose={() => setIsOpen(false)} headline="Upload Terms">
                <Button {...styles.downloadTemplateButton} onClick={downloadTemplateHandler}>
                    {translate("Download Template")}
                </Button>
                <FileUpload
                    {...styles.fileUploader}
                    inputRef={fileUploadRef}
                    fileName={file?.name || ""}
                    accept=".xls,.xlsx,.csv"
                    label={translate("Upload a File")}
                    labelPosition="top"
                    error={incorrectFileExtension ? translate("must be .xls, .xlsx or .csv") : undefined}
                    onFileChange={fileChangeHandler}
                    uid="accessorialUploadFileUpload"
                />
                <Button
                    {...styles.uploadFileButton}
                    disabled={!file || isUploading}
                    onClick={uploadFileHandler}
                    data-test-selector="orderUpload_UploadFile"
                >
                    {isUploading && (
                        <>
                            <LoadingSpinner {...styles.spinner} />
                            <Typography {...styles.uploadingButtonLabel}>{translate("Uploading")}</Typography>
                        </>
                    )}
                    {!isUploading && (
                        <Typography {...styles.uploadFileButtonLabel}>{translate("Upload File")}</Typography>
                    )}
                </Button>
            </Modal>
        </>
    );
};

const importTermsWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ImportTerms),
    definition: {
        group: "Basic",
    },
};

export default importTermsWidget;
